/**
 * QGIS-equivalent parameters for TiTiler's `hillshade` algorithm.
 *
 * `titiler.core.algorithm.dem.HillShade` is the widely-copied numpy snippet:
 *
 *     x, y  = numpy.gradient(array)
 *     slope  = pi/2 - arctan(hypot(x, y))
 *     aspect = arctan2(-x, y)
 *     shaded = sin(alt)*sin(slope)
 *            + cos(alt)*cos(slope)*cos(radians(360 - azimuth) - aspect)
 *     gray   = 255 * (shaded + 1) / 2
 *
 * Three things in there make it disagree with what QGIS paints for the same
 * DEM (`QgsHillshadeRenderer`, which reproduces `gdaldem hillshade`):
 *
 *  1. `numpy.gradient` differences neighbouring SAMPLES, so its slope is
 *     "elevation per pixel"; GDAL divides by the cell size to get elevation
 *     per ground metre. Tiles resample the COG, so the shading strength then
 *     swings with zoom — for one 10° slope TiTiler's gray runs 221 → 253 → 220
 *     between 0.25 and 256 m/px while QGIS holds 209 throughout.
 *     `z_exaggeration` scales both gradients, so passing
 *     `zFactor / groundMetresPerPixel` supplies exactly the missing division.
 *
 *  2. `azimuth` is not a compass bearing. Expanding the formula gives an
 *     illumination direction of `270° - azimuth` where GDAL's is
 *     `360° - azimuth`, so light lands 90° away from where it is asked for:
 *     `azimuth: 315` lit the terrain from the south-west. TiTiler's own
 *     default of 45 is the value that means QGIS's 315.
 *
 *  3. `255 * (shaded + 1) / 2` maps illumination [-1, 1] onto [0, 255] where
 *     GDAL maps [0, 1] (`1 + 254 * cang`). That halves the contrast and lifts
 *     everything — flat ground lands on 218 instead of 181. `rescale` runs
 *     after the algorithm, and `2v - 255` is the exact inverse, so
 *     {@link HILLSHADE_RESCALE} undoes it.
 *
 * With all three applied, mean per-pixel error against `gdaldem hillshade`
 * drops from 39–121 gray levels to 2–4 across 0.5–32 m/px.
 */

import { upsertQueryParam } from '@/utils/titilerRasterProfile';

/** QGIS Layer Properties → Symbology → Hillshade defaults. */
export const QGIS_HILLSHADE_AZIMUTH_DEG = 315;
export const QGIS_HILLSHADE_ALTITUDE_DEG = 45;
export const QGIS_HILLSHADE_Z_FACTOR = 1;

/**
 * `rescale` that turns TiTiler's [-1, 1] → [0, 255] mapping back into GDAL's
 * [0, 1] → [0, 255]. Anything below 127.5 is illumination the sun never
 * reaches, which GDAL floors at black too.
 */
export const HILLSHADE_RESCALE = '127.5,255';

/**
 * Ground resolution the tile template is written for. Engines resolve the real
 * one per request (see {@link resolveHillshadeTileUrl}); this only decides what
 * an unresolved request falls back to, so it is kept at the 1 m/px that local
 * converter DEMs are usually produced at.
 */
export const HILLSHADE_TEMPLATE_RESOLUTION_M = 1;

/**
 * TiTiler's HillShade trims `buffer` px off each edge after differencing, so
 * the neighbouring-tile context that a 3×3 kernel needs never shows up as a
 * seam. It does NOT widen the read to pay for that, so the endpoint has to be
 * asked for the same buffer: without `&buffer=3` a 256 px tile comes back
 * 250 px, and the engines stretch it over the full tile footprint — hillshade
 * ~2.4% larger than the DEM under it, with a visible step at every tile edge.
 */
export const HILLSHADE_BUFFER_PX = 3;

/** Width of the EPSG:3857 extent — one WebMercatorQuad tile row at z0. */
const WEB_MERCATOR_EXTENT_M = 40075016.685578488;

const DEFAULT_TILE_SIZE = 256;

/** Web Mercator is undefined past this latitude; cos() there is ~0 anyway. */
const MAX_MERCATOR_LATITUDE = 85.05112878;

/** TiTiler's `z_exaggeration` field bounds (`ge=1e-6, le=1e6`). */
const MIN_Z_EXAGGERATION = 1e-6;
const MAX_Z_EXAGGERATION = 1e6;

export type HillshadeAlgorithmParams = {
  readonly azimuth: number;
  readonly angle_altitude: number;
  readonly buffer: number;
  readonly z_exaggeration: number;
  /**
   * Operator Z-factor (QGIS / gdaldem). Persisted on the URL so per-tile
   * resolution rewrites can recover it after overwriting `z_exaggeration`.
   * TiTiler ignores unknown fields (`extra: allow`); only `z_exaggeration`
   * drives the algorithm.
   */
  readonly z_factor: number;
};

export type HillshadeParamOptions = {
  /** Ground metres per array pixel the algorithm will run on. */
  readonly groundResolutionM?: number;
  /** Compass bearing of the light, as QGIS/GDAL mean it. */
  readonly azimuthDeg?: number;
  readonly altitudeDeg?: number;
  readonly zFactor?: number;
};

function clamp(value: number, lo: number, hi: number): number {
  if (!Number.isFinite(value)) return lo;
  return Math.max(lo, Math.min(hi, value));
}

/** TiTiler `azimuth` that lights the terrain from the given compass bearing. */
export function titilerHillshadeAzimuth(
  qgisAzimuthDeg: number = QGIS_HILLSHADE_AZIMUTH_DEG,
): number {
  const bearing = Number.isFinite(qgisAzimuthDeg)
    ? qgisAzimuthDeg
    : QGIS_HILLSHADE_AZIMUTH_DEG;
  return (((Math.round(bearing) + 90) % 360) + 360) % 360;
}

/** Latitude at the centre of WebMercatorQuad row `y`. */
export function tileCenterLatitude(y: number, zoom: number): number {
  const rows = Math.pow(2, zoom);
  const n = Math.PI - (2 * Math.PI * (y + 0.5)) / rows;
  return (180 / Math.PI) * Math.atan(Math.sinh(n));
}

/**
 * Ground metres per pixel of the array TiTiler runs the algorithm on: the
 * tile's Web Mercator span divided by `tilesize`, narrowed by the Mercator
 * scale factor so the value is in real metres like a projected QGIS project.
 */
export function tileGroundResolution(
  zoom: number,
  tileSize: number = DEFAULT_TILE_SIZE,
  latitudeDeg = 0,
): number {
  const size = Number.isFinite(tileSize) && tileSize > 0 ? tileSize : DEFAULT_TILE_SIZE;
  const mercator = WEB_MERCATOR_EXTENT_M / (Math.pow(2, zoom) * size);
  const lat = clamp(latitudeDeg, -MAX_MERCATOR_LATITUDE, MAX_MERCATOR_LATITUDE);
  return mercator * Math.cos((lat * Math.PI) / 180);
}

/** Clamp an operator Z-factor to a sensible QGIS-like range. */
export function clampHillshadeZFactor(value: number): number {
  if (!Number.isFinite(value)) return QGIS_HILLSHADE_Z_FACTOR;
  return clamp(value, 0.1, 10);
}

/** `algorithm_params` payload for a hillshade at the given ground resolution. */
export function hillshadeAlgorithmParams(
  options: HillshadeParamOptions = {},
): HillshadeAlgorithmParams {
  const resolution = options.groundResolutionM ?? HILLSHADE_TEMPLATE_RESOLUTION_M;
  const zFactor = clampHillshadeZFactor(options.zFactor ?? QGIS_HILLSHADE_Z_FACTOR);
  return {
    azimuth: titilerHillshadeAzimuth(options.azimuthDeg),
    angle_altitude: options.altitudeDeg ?? QGIS_HILLSHADE_ALTITUDE_DEG,
    buffer: HILLSHADE_BUFFER_PX,
    z_factor: zFactor,
    z_exaggeration: clamp(zFactor / resolution, MIN_Z_EXAGGERATION, MAX_Z_EXAGGERATION),
  };
}

export function hillshadeAlgorithmParamsJson(options: HillshadeParamOptions = {}): string {
  return JSON.stringify(hillshadeAlgorithmParams(options));
}

/** True when this TiTiler URL (template or resolved) asks for hillshade. */
export function isHillshadeTileUrl(url: string): boolean {
  const value = /[?&]algorithm=([^&]*)/.exec(url)?.[1];
  if (!value) return false;
  try {
    return decodeURIComponent(value).toLowerCase() === 'hillshade';
  } catch {
    return value.toLowerCase() === 'hillshade';
  }
}

/** Trailing `/{z}/{x}/{y}` of a resolved XYZ tile path, extension optional. */
const TILE_COORD_RE = /\/(\d+)\/(\d+)\/(\d+)(?:@\d+x)?(?:\.[A-Za-z0-9]+)?$/;

function readTileSize(url: string): number {
  const size = Number(/[?&]tilesize=(\d+)/.exec(url)?.[1]);
  return Number.isFinite(size) && size > 0 ? size : DEFAULT_TILE_SIZE;
}

function readAlgorithmParams(url: string): Record<string, unknown> | null {
  const raw = /[?&]algorithm_params=([^&]*)/.exec(url)?.[1];
  if (!raw) return null;
  try {
    const parsed = JSON.parse(decodeURIComponent(raw)) as unknown;
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed)
      ? (parsed as Record<string, unknown>)
      : null;
  } catch {
    return null;
  }
}

/**
 * Operator Z-factor carried on a hillshade URL (template or resolved tile).
 * Prefers the explicit `z_factor` we persist. Falls back to `z_exaggeration`
 * only on unresolved templates (built at 1 m/px, so the two are equal).
 */
export function readHillshadeZFactorFromUrl(url: string): number {
  const params = readAlgorithmParams(url);
  if (!params) return QGIS_HILLSHADE_Z_FACTOR;
  const zFactor = Number(params.z_factor);
  if (Number.isFinite(zFactor)) return clampHillshadeZFactor(zFactor);
  if (url.includes('{z}') || url.includes('{y}')) {
    const exaggeration = Number(params.z_exaggeration);
    if (Number.isFinite(exaggeration)) return clampHillshadeZFactor(exaggeration);
  }
  return QGIS_HILLSHADE_Z_FACTOR;
}

/**
 * Rewrite `z_exaggeration` on a tile URL whose `{z}/{x}/{y}` an engine has
 * already substituted, so the algorithm differences ground metres instead of
 * samples. Returns the input untouched for anything that is not a resolved
 * hillshade tile request. Idempotent for a given tile coordinate + Z-factor:
 * the operator Z-factor is read from the URL (or `options`) and re-applied.
 */
export function resolveHillshadeTileUrl(
  url: string,
  options: Omit<HillshadeParamOptions, 'groundResolutionM'> = {},
): string {
  if (!isHillshadeTileUrl(url)) return url;
  const queryAt = url.indexOf('?');
  const path = queryAt < 0 ? url : url.slice(0, queryAt);
  const coord = TILE_COORD_RE.exec(path);
  if (!coord) return url;

  const zoom = Number(coord[1]);
  const row = Number(coord[3]);
  if (!Number.isFinite(zoom) || !Number.isFinite(row)) return url;

  const zFactor = options.zFactor ?? readHillshadeZFactorFromUrl(url);

  return upsertQueryParam(
    url,
    'algorithm_params',
    hillshadeAlgorithmParamsJson({
      ...options,
      zFactor,
      groundResolutionM: tileGroundResolution(
        zoom,
        readTileSize(url),
        tileCenterLatitude(row, zoom),
      ),
    }),
  );
}

type TileUrlFunction = (
  tileCoord: number[],
  pixelRatio: number,
  projection: unknown,
) => string | undefined;

type TileUrlSource = {
  getTileUrlFunction?: () => TileUrlFunction;
  setTileUrlFunction?: (fn: TileUrlFunction) => void;
};

/**
 * Teach an OpenLayers `XYZ` source — used directly by the Canvas adapter and
 * wrapped in a `TiledImageSource` by Giro3D — to resolve hillshade
 * `z_exaggeration` per tile. No-op for non-hillshade templates and for stubs
 * that do not expose a tile URL function.
 */
export function attachHillshadeTileResolution(source: unknown, templateUrl: string): void {
  if (!isHillshadeTileUrl(templateUrl)) return;
  const olSource = source as TileUrlSource | null;
  const base = olSource?.getTileUrlFunction?.();
  if (typeof base !== 'function' || typeof olSource?.setTileUrlFunction !== 'function') return;
  olSource.setTileUrlFunction((tileCoord, pixelRatio, projection) => {
    const resolved = base(tileCoord, pixelRatio, projection);
    return typeof resolved === 'string' ? resolveHillshadeTileUrl(resolved) : resolved;
  });
}
